import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Component, ViewContainerRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnCurrentUser, SnSessionService } from '@lib/auth';
import { FsAxonService } from '@lib/axon/services/axon.service';
import { AxonRoutes } from '@lib/common/constants';

import {
  MfaListDialogComponent,
  SnChangeBadgeDialogComponent,
  SnChangeEmailDialogComponent,
  SnChangeNameDialogComponent,
  SnChangePasswordDialogComponent,
  SnChangePhoneDialogComponent,
  SnChangeTitleDialogComponent,
  TwoFactorAuthenticationDialogComponent,
} from '../../components';

@Component({
  selector: 'sn-account-settings-page',
  templateUrl: './account-settings.container.html',
  styleUrls: ['./account-settings.container.scss'],
})
export class SnAccountSettingsContainer implements OnInit, OnDestroy {
  public mfaEnabled: boolean;
  public isLoadingMfa: boolean = false;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _viewContainerRef: ViewContainerRef,
    public readonly currentUser: SnCurrentUser,
    private readonly _http: HttpClient,
    private readonly _sessionService: SnSessionService,
    private readonly _axonService: FsAxonService,
  ) {
    this._getUserProfile();
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public editName(): void {
    this._matDialog.open(SnChangeNameDialogComponent, {
      viewContainerRef: this._viewContainerRef,
    });
  }

  get isPersonalInfoReadOnly(): boolean {
    return this.currentUser.isExternallyManaged && !this._sessionService.isAxonSso;
  }

  get externalProfileUrl(): string {
    return this._axonService.orgSpecificBaseUrl
      ? new URL(AxonRoutes.CURRENT_USER_PROFILE, this._axonService.orgSpecificBaseUrl,).toString()
      : null
  }

  public changeEmail(): void {
    this._matDialog.open(SnChangeEmailDialogComponent, {
      viewContainerRef: this._viewContainerRef,
    });
  }

  public changePhone(): void {
    this._matDialog.open(SnChangePhoneDialogComponent, {
      viewContainerRef: this._viewContainerRef,
    });
  }

  public changeTitle(): void {
    this._matDialog.open(SnChangeTitleDialogComponent, {
      viewContainerRef: this._viewContainerRef,
    });
  }

  public changeBadge(): void {
    this._matDialog.open(SnChangeBadgeDialogComponent, {
      viewContainerRef: this._viewContainerRef,
    });
  }

  public changePassword(): void {
    this._matDialog.open(SnChangePasswordDialogComponent);
  }

  public enableTwoFa(): void {
    this._matDialog
      .open(TwoFactorAuthenticationDialogComponent, {
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._getUserProfile();
      });
  }

  public manageMfaDevices(): void {
    this._matDialog
      .open(MfaListDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._getUserProfile();
      });
  }

  public startAxon(): void {
    this._getIDPConfig();
  }

  private _getIDPConfig(): void {
    const agencyId = 'rx';
    const regionCode = '.ag1';
    const state = Math.random().toString(36).slice(2, 14);
    localStorage.setItem('axonstate', state);
    this._http
      .get(`https://${agencyId}${regionCode}.evidence.com/api/oauth2/configuration`)
      .pipe(takeUntil(this._destroy$))
      .subscribe((resp) => {
        localStorage.setItem('axonconfig', JSON.stringify(resp));
        this._openOauthWindow();
      });
  }

  private async _openOauthWindow(): Promise<void> {
    const agencyId = 'rx';
    const regionCode = '.ag1';
    const state = localStorage.getItem('axonstate');
    const clientID = '7be92f30-99c6-417d-9847-408a79332c83';
    const redirectURI = 'http://localhost:8000/oauth/axonrespond';
    const validChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let array = new Uint8Array(64);
    window.crypto.getRandomValues(array);
    array = array.map((x) => validChars.charCodeAt(x % validChars.length));
    const codeChallenge = String.fromCharCode.apply(null, array);
    localStorage.setItem('axonchallenge', codeChallenge);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeChallenge));
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    const fullUrl = `https://id${regionCode}.evidence.com/oauth/authorize?response_type=code&redirect_uri=${redirectURI}&client_id=${clientID}&state=${state}&domain=${agencyId}${regionCode}.evidence.com&code_challenge=${challenge}&code_challenge_method=S256`;
    window.open(fullUrl);
  }

  private _getUserProfile(): void {
    this.isLoadingMfa = true;
    this.currentUser
      .getProfile()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (user) => {
          this.mfaEnabled = user.mfaEnabled;
          this.isLoadingMfa = false;
        },
        error: () => {
          this.isLoadingMfa = false;
        },
      });
  }
}
